import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ETAPAS, TIPO_LABEL } from "@/lib/stages";

const ETAPAS_IMPORT = ETAPAS.filter((e) => !["agendado", "perdido"].includes(e.id));

export function ImportModal({
  open, onClose, importFile, setImportFile, importEtapa, setImportEtapa,
  importTipo, setImportTipo, importing, onSubmit, pipelineId, pipelineName,
}) {
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const resetAndClose = () => {
    setPreview(null);
    onClose();
  };

  const analyze = async () => {
    if (!importFile) return toast.error("Selecione um arquivo");
    if (!pipelineId) return toast.error("Selecione uma campanha");
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("pipeline_id", pipelineId);
      const { data } = await api.post("/contacts/import/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(errorMessage(e, "Erro ao analisar"));
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadInvalid = () => {
    if (!preview?.invalid_list?.length) return;
    const lines = [["nome", "telefone", "motivo"]].concat(preview.invalid_list.map((r) => [r.nome || "", r.telefone || "", r.motivo || ""]));
    const csv = lines.map((row) => row.map((c) => `"${(c || "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "telefones_invalidos.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !importing) resetAndClose(); }}>
      <DialogContent className="max-w-md" data-testid="import-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-base font-semibold">Importar leads</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label className="text-xs">Arquivo (CSV ou Excel) *</Label>
            <input
              data-testid="import-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => { setImportFile(e.target.files?.[0] || null); setPreview(null); }}
              className="w-full h-8 text-xs text-zinc-400 file:mr-2 file:h-8 file:px-3 file:rounded file:border-0 file:text-xs file:bg-zinc-800 file:text-zinc-200 hover:file:bg-zinc-700 cursor-pointer"
            />
          </div>
          <div>
            <Label className="text-xs">Campanha destino</Label>
            <p className="text-xs text-zinc-200 mt-1">{pipelineName || "—"}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Etapa inicial</Label>
              <Select value={importEtapa} onValueChange={setImportEtapa}>
                <SelectTrigger data-testid="import-etapa-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ETAPAS_IMPORT.map((e) => (<SelectItem key={e.id} value={e.id} className="text-xs">{e.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={importTipo} onValueChange={setImportTipo}>
                <SelectTrigger data-testid="import-tipo-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="novo" className="text-xs">{TIPO_LABEL.novo}</SelectItem>
                  <SelectItem value="base" className="text-xs">{TIPO_LABEL.base}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[10px] text-zinc-600">Colunas aceitas: Nome, Telefone, Instagram, Origem, Responsável, Intenção.</p>

          {!preview && (
            <Button type="button" data-testid="import-analyze-btn" onClick={analyze} disabled={!importFile || analyzing} className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 h-8 text-xs font-semibold disabled:opacity-50">
              {analyzing ? "Analisando..." : "Analisar arquivo"}
            </Button>
          )}

          {preview && (
            <div className="rounded-md border border-zinc-800 bg-zinc-900 p-3 space-y-2" data-testid="import-preview">
              <p className="text-xs text-zinc-300 font-medium">Resumo da importação</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between"><span className="text-zinc-500">Total no arquivo</span><span className="font-semibold text-zinc-200 tabular-nums">{preview.total}</span></div>
                <div className="flex justify-between"><span className="text-green-400">Válidos</span><span className="font-semibold text-green-400 tabular-nums" data-testid="preview-validos">{preview.validos}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Já existentes</span><span className="font-semibold text-amber-400 tabular-nums" data-testid="preview-duplicados">{preview.duplicados}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Inválidos</span><span className="font-semibold text-red-400 tabular-nums" data-testid="preview-invalidos">{preview.invalidos}</span></div>
                {preview.opt_outs > 0 && (
                  <div className="flex justify-between col-span-2"><span className="text-zinc-500">Opt-out (bloqueados)</span><span className="font-semibold text-zinc-400 tabular-nums">{preview.opt_outs}</span></div>
                )}
              </div>
              {preview.invalidos > 0 && (
                <button type="button" onClick={downloadInvalid} className="w-full text-[11px] text-amber-400 hover:text-amber-300 underline" data-testid="download-invalid">
                  Baixar lista de inválidos ({preview.invalidos})
                </button>
              )}
            </div>
          )}

          <Button
            data-testid="import-submit-btn"
            type="submit"
            disabled={!importFile || importing || !preview || preview.validos === 0}
            className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-600 h-8 text-xs font-semibold disabled:opacity-50"
          >
            {importing ? "Importando..." : preview ? `Importar ${preview.validos} válido${preview.validos !== 1 ? "s" : ""}` : "Analise primeiro"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
